<template>
  <v-dialog v-model="isOpen" width="860" persistent>
    <v-card class="pa-0">
      <div class="d-flex align-start justify-space-between px-5 pt-5">
        <h1>{{ report ? 'Editar análise' : 'Nova análise' }}</h1>
        <mf-icon icon="close" size="24" color="cinza-d20" class="cursor-pointer" @click="isOpen = false" />
      </div>
      <div class="scrollable-container pa-5 pt-0">
        <h2>Configurações da análise</h2>
        <p>Selecione o tipo da análise e as configurações de período do mesmo.</p>

        <mf-select
          v-model="reportType"
          class="select-width mb-5"
          hide-details
          :disabled="report"
          :items="reportTypes"
          label="Tipo de análise"
          item-text="name"
          item-value="value"
          dense
        >
          <template #prepend-inner>
            <mf-icon icon="search" />
          </template>
        </mf-select>

        <mf-toggle v-model="recurrent" label="Análise recorrente" :disabled="report" class="ma-0" hide-details />
        <p class="toggle-description">Ativando a opção de análise recorrente você configura a análise e os filtros apenas uma vez e ela sempre será gerada.</p>

        <div class="recurrence-container">
          <mf-select
            v-if="recurrent"
            v-model="recurrence"
            class="select-width"
            hide-details
            :items="recurrences"
            label="Recorrência"
            item-text="name"
            item-value="value"
            dense
            :disabled="recurrent && reportType === 'RFV'"
          />

          <div v-if="recurrent && recurrence === 'MONTHLY'" class="d-flex align-center">
            <mf-icon icon="info" color="cinza-0" size="20" />
            <p>Na recorrência mensal, a análise sempre será gerada assim que a plataforma obter os dados de um mês fechado.</p>
          </div>

          <div v-if="recurrent && recurrence === 'WEEKLY'" class="d-flex align-center">
            <mf-select
              v-model="weeklyRecurrence"
              class="select-width"
              hide-details
              :items="weeklyRecurrences"
              label="Dia inicial e final da semana"
              item-text="name"
              item-value="value"
              dense
            />
          </div>

          <div v-if="!recurrent">
            <label>Período Analisado</label>
            <mf-date-range-picker
              :key="analyzedPeriodKey"
              :error-messages="analyzedPeriodErrorMessages"
              dense
              :initial-date.sync="analyzedPeriodInitialDate"
              :final-date.sync="analyzedPeriodFinalDate"
              show-days-counter
              icon
              :max="analyzedPeriodMaxDate"
              end-date-future
              @input="analyzedPeriodKey++"
            />
          </div>
          <div v-if="reportType === 'RFV' && !recurrent">
            <label>Período Referência</label>
            <mf-date-range-picker
              :error-messages="referencePeriodErrorMessages"
              dense
              :initial-date.sync="referencePeriodInitialDate"
              :final-date.sync="referencePeriodFinalDate"
              show-days-counter
              icon
              :max="referencePeriodMaxDate"
              end-date-future
            />
          </div>
        </div>

        <hr />

        <h2>Filtros</h2>
        <p>Selecione os filtros que serão aplicados nessa análise e dê um nome para que fique salva no histórico.</p>

        <v-text-field
          v-model="filters.name"
          :error-messages="$v.filters.name.$error ? 'Insira um nome válido.' : ''"
          class="filter-name"
          outlined
          dense
          width="50%"
          label="Nome do filtro"
        />

        <new-report-dialog-filters :filters.sync="filters" :disable-campaigns="recurrent" :hide-offer-button="reportType === 'RFV'" />
      </div>
      <mf-action-buttons
        class="pa-5"
        :primary-button="{
          text: report ? 'Salvar análise' : 'Gerar análise',
          action: generateAnalysis,
          isVisible: true,
          isLoading: loading,
          tooltip: isLimitReached,
          isDisabled: isLimitReached,
          tooltipMessage: 'Limite de relatórios deste tipo atingido.',
        }"
        :extra-button="{
          text: 'Cancelar',
          action: () => (isOpen = false),
          isVisible: true,
          isLoading: loading,
        }"
      />
    </v-card>
  </v-dialog>
</template>

<script>
import NewReportDialogFilters from './NewReportDialogFilters.vue'
import { minLength, required } from 'vuelidate/lib/validators'
import { notOnlySpace } from '@/helpers/validators.js'
import { DateTime } from 'luxon'
import { mapActions, mapGetters, mapState } from 'vuex'

export default {
  name: 'NewReportDialog',
  components: { NewReportDialogFilters },
  props: {
    value: {
      type: Boolean,
      default: false,
    },
    report: {
      type: Object,
      default: () => {},
    },
  },
  data: () => ({
    loading: false,
    recurrent: true,
    reportType: 'RFV',
    recurrence: 'MONTHLY',
    analyzedPeriodInitialDate: '',
    analyzedPeriodFinalDate: '',
    referencePeriodInitialDate: '',
    referencePeriodFinalDate: '',
    weeklyRecurrence: 'MONDAY',
    filters: {
      name: '',
      campaigns: [],
      units: [],
      products: [],
      personType: [],
    },
    analyzedPeriodKey: 0,
  }),
  computed: {
    ...mapGetters(['hubAnalyticsLimits']),
    ...mapState({
      lastUpdate: (state) => state.lastUpdate,
    }),
    isOpen: {
      get() {
        return this.value
      },
      set(value) {
        this.$emit('input', value)
      },
    },
    hasRfvPermission() {
      return this.$permissions.havePagePermission('clientes/rfv')
    },
    hasDrePermission() {
      return this.$permissions.havePagePermission('empresa/faturamento_incremental') && this.$getAllowedModules()['offer_management']
    },
    reportTypes() {
      return [
        ...(this.hasRfvPermission ? [{ value: 'RFV', name: 'Classificação RFV' }] : []),
        ...(this.hasDrePermission ? [{ value: 'DRE', name: 'Faturamento incremental' }] : []),
      ]
    },
    recurrences() {
      let recurrences = [{ value: 'MONTHLY', name: 'Mensal' }]
      const weekly = { value: 'WEEKLY', name: 'Semanal' }

      return this.reportType === 'RFV' ? recurrences : [...recurrences, weekly]
    },
    analyzedPeriodErrorMessages() {
      const hasError = this.$v.analyzedPeriodInitialDate.$error || this.$v.analyzedPeriodFinalDate.$error
      return hasError ? 'Insira um período analisado.' : ''
    },
    referencePeriodErrorMessages() {
      const hasError = this.$v.referencePeriodInitialDate.$error || this.$v.referencePeriodFinalDate.$error
      return hasError ? ' Insira um período referência.' : ''
    },
    weeklyRecurrences() {
      return [
        {
          value: 'MONDAY',
          name: 'Segunda-feira / Domingo',
        },
        {
          value: 'TUESDAY',
          name: 'Terça-feira / Segunda-feira',
        },
        {
          value: 'WEDNESDAY',
          name: 'Quarta-feira / Terça-feira',
        },
        {
          value: 'THURSDAY',
          name: 'Quinta-feira / Quarta-feira',
        },
        {
          value: 'FRIDAY',
          name: 'Sexta-feira / Quinta-feira',
        },
        {
          value: 'SATURDAY',
          name: 'Sábado / Sexta-feira',
        },
        {
          value: 'SUNDAY',
          name: 'Domingo / Sábado',
        },
      ]
    },
    isLimitReached() {
      if (!this.report) {
        const key = this.recurrent ? 'recurrent' : 'notRecurrent'
        return (
          this.hubAnalyticsLimits[this.reportType.toLowerCase()][key].createdTotal >= this.hubAnalyticsLimits[this.reportType.toLowerCase()][key].limitTotal
        )
      } else return false
    },
    analyzedPeriodMaxDate() {
      if (!this.lastUpdate?.last_updated) return ''
      if (!this.analyzedPeriodInitialDate && !this.analyzedPeriodFinalDate) {
        return DateTime.fromFormat(this.lastUpdate.last_updated.toString(), 'yyyyMMdd').plus({ days: -1 }).toFormat('yyyy-MM-dd')
      }

      return DateTime.fromFormat(this.lastUpdate.last_updated.toString(), 'yyyyMMdd').toFormat('yyyy-MM-dd')
    },
    referencePeriodMaxDate() {
      if (!this.lastUpdate?.last_updated) return ''
      if (!this.referencePeriodInitialDate && !this.referencePeriodFinalDate) {
        return DateTime.fromFormat(this.lastUpdate.last_updated.toString(), 'yyyyMMdd').plus({ days: -1 }).toFormat('yyyy-MM-dd')
      }

      return DateTime.fromFormat(this.lastUpdate.last_updated.toString(), 'yyyyMMdd').toFormat('yyyy-MM-dd')
    },
    ungroupedProducts() {
      return this.filters.products.filter((product) => !product.isGroup).map((product) => product.sku)
    },
    groupedProducts() {
      const groupIds = this.filters.products.filter((product) => product.isGroup && product.groupInfo).map((product) => product.groupInfo._id)

      return [...new Set(groupIds)]
    },
  },
  watch: {
    'filters.campaigns'(newValue) {
      if (newValue.length > 0) this.handleSelectOfferSales(newValue)
    },
    recurrent(newValue) {
      if (newValue) this.filters.campaigns = []
    },
    reportType(newValue) {
      if (newValue === 'RFV') this.filters.campaigns = []
    },
  },
  mounted() {
    if (!this.hasRfvPermission) this.reportType = 'DRE'
    if (this.report) {
      this.filters = this.report.filters
      this.analyzedPeriodInitialDate = this.report.analyzedPeriodInitialDate || ''
      this.analyzedPeriodFinalDate = this.report.analyzedPeriodFinalDate || ''
      this.referencePeriodInitialDate = this.report.referencePeriodInitialDate || ''
      this.referencePeriodFinalDate = this.report.referencePeriodFinalDate || ''
      this.recurrence = this.report.recurrence
      this.recurrent = this.report.recurrent
      this.reportType = this.report.reportType
      this.weeklyRecurrence = this.report.weeklyRecurrence || 'MONDAY'
    }
  },
  methods: {
    ...mapActions(['generateHubAnalysis']),
    getClientsType() {
      if (this.filters?.personType?.length === 2) return ['LEGAL_PERSON', 'NATURAL_PERSON']
      else if (this.filters?.personType?.length === 1) return this.filters.personType[0] === 'J' ? ['LEGAL_PERSON'] : ['NATURAL_PERSON']
      return []
    },
    async generateAnalysis() {
      this.$v.$reset()
      this.$v.$touch()

      if (this.$v.$error) {
        this.$snackbar({ message: 'Preencha todos os campos corretamente', snackbarColor: 'error' })
        return
      }

      const clientsType = this.getClientsType()
      const variables = {
        input: {
          type: this.reportType,
          model: this.recurrent ? 'RECURRENT' : 'NOT_RECURRENT',
          title: this.filters.name,
          filters: {
            units: this.filters.units,
            products: this.ungroupedProducts,
            productsGroups: this.groupedProducts,
            clientsType,
            offersScheduleIds: this.reportType === 'DRE' && !this.recurrent ? this.filters.campaigns.map((campaign) => campaign._id) : undefined,
          },
          ...(this.recurrent
            ? {
                recurrent: {
                  modelType: this.recurrence,
                  ...(this.recurrence === 'WEEKLY' && { initialWeekDay: this.weeklyRecurrence }),
                },
              }
            : {
                notRecurrent: {
                  analyzedPeriod: {
                    initialDate: this.analyzedPeriodInitialDate,
                    finalDate: this.analyzedPeriodFinalDate,
                  },
                  referencePeriod:
                    this.reportType === 'RFV'
                      ? {
                          initialDate: this.referencePeriodInitialDate,
                          finalDate: this.referencePeriodFinalDate,
                        }
                      : null,
                },
              }),
        },
      }
      if (this.report) variables.input.id = this.report.id
      try {
        this.loading = true
        const status = await this.generateHubAnalysis(variables)
        if ((status && status === 'ERROR') || status instanceof Error) throw new Error()
        this.$snackbar({ message: this.report ? 'Análise editada com sucesso' : 'Análise gerada com sucesso', snackbarColor: 'success' })
        this.$emit('analysis-generated')
        this.isOpen = false
      } catch (error) {
        console.log(error)
        this.$snackbar({ message: 'Ocorreu um erro ao gerar análise', snackbarColor: 'error' })
      } finally {
        this.loading = false
      }
    },
    handleSelectOfferSales(offerSales) {
      const unitsSet = new Set()
      let lowestStartDate = null
      let highestEndDate = null
      let allProducts = []
      offerSales.forEach((offerSale) => {
        offerSale.units.forEach((unit) => unitsSet.add(unit.id_erp))
        allProducts = [...allProducts, ...offerSale.products]

        const startDate = DateTime.fromISO(offerSale.start_date)
        if (!lowestStartDate || startDate < lowestStartDate) {
          lowestStartDate = startDate
        }

        const endDate = DateTime.fromISO(offerSale.end_date)
        if (!highestEndDate || endDate > highestEndDate) {
          highestEndDate = endDate
        }
      })

      this.filters.units = [...unitsSet]
      this.filters.products = Array.from(new Map(allProducts.map((item) => [item._id, item])).values())
      this.analyzedPeriodInitialDate =
        DateTime.fromISO(lowestStartDate.toISODate()) > DateTime.fromISO(this.maxDate) ? this.maxDate : lowestStartDate.toISODate()
      this.analyzedPeriodFinalDate = DateTime.fromISO(highestEndDate.toISODate()) > DateTime.fromISO(this.maxDate) ? this.maxDate : highestEndDate.toISODate()
      this.analyzedPeriodKey++
    },
  },
  validations() {
    return {
      filters: {
        name: {
          required,
          minLength: minLength(2),
          notOnlySpace,
        },
      },
      ...(!this.recurrent
        ? {
            analyzedPeriodInitialDate: {
              required,
              notOnlySpace,
            },
            analyzedPeriodFinalDate: {
              required,
              notOnlySpace,
            },
          }
        : {}),
      ...(!this.recurrent && this.reportType === 'RFV'
        ? {
            referencePeriodInitialDate: {
              required,
              notOnlySpace,
            },
            referencePeriodFinalDate: {
              required,
              notOnlySpace,
            },
          }
        : {}),
    }
  },
}
</script>

<style lang="scss" scoped>
h1 {
  font-size: 22px;
  color: $cinza-d10;
  font-weight: 700;
  margin-bottom: 20px;
}

h2 {
  font-size: 16px;
  color: $cinza-d10;
  font-weight: 700;
  margin-bottom: 8px;
}

p {
  font-size: 14px;
  color: $cinza-0;
  font-weight: 400;
  margin-bottom: 12px;
}

.toggle-description {
  max-width: 526px;
}

.select-width,
::v-deep .mf-date-range-picker-dense {
  width: 280px;
}

.recurrence-container {
  display: flex;
  align-items: center;
  gap: 32px;

  p {
    max-width: 366px;
    margin: 0 0 0 8px;
  }

  label {
    font-weight: 700;
    font-size: 14px;
    color: $cinza-d10;
    display: block;
    margin-bottom: 8px;
  }
}

hr {
  border-top: 1px solid $cinza-l60;
  margin: 20px 0;
}

.filter-name {
  width: 50%;
}

.scrollable-container {
  max-height: 370px;
  overflow-y: auto;
}
</style>
